import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Loader2,
  Pencil,
  RefreshCw,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { RouterInputs, RouterOutputs, trpc, useTRPCClient } from "@/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type PayableItem =
  RouterOutputs["providerPayout"]["listPayableItems"]["items"][number];
type RecentPayout =
  RouterOutputs["providerPayout"]["listRecentPayouts"]["items"][number];
type ProviderPayoutStatus = NonNullable<
  RouterInputs["providerPayout"]["listRecentPayouts"]["status"]
>;

const ALL_VALUE = "all";
const PAGE_SIZE = 25;
const RECENT_PAGE_SIZE = 10;
const EMPTY_PAYABLE_ITEMS: PayableItem[] = [];
const EMPTY_RECENT_PAYOUTS: RecentPayout[] = [];
type BulkAction = "dismiss" | "external-paid";

const PAYOUT_STATUS_OPTIONS = [
  { value: "processing", label: "Processing" },
  { value: "transferred", label: "Transferred" },
  { value: "payout_pending", label: "Payout Pending" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "canceled", label: "Canceled" },
  { value: "reversed", label: "Reversed" },
] as const;

function formatCurrency(amount: string | number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(typeof amount === "string" ? Number(amount) : amount);
}

function formatDate(value: Date | string | null) {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function formatDateTime(value: Date | string | null) {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function sourceTypeLabel(sourceType: PayableItem["sourceType"]) {
  return sourceType === "time_entry" ? "Time entry" : "Expense";
}

function shortId(id: string | null) {
  return id ? id.slice(0, 8) : "-";
}

function selectionKey(item: PayableItem) {
  return `${item.providerId}:${item.organizationBusinessUnitId ?? ""}`;
}

function statusVariant(status: RecentPayout["status"]) {
  return status === "failed" ? "destructive" : "outline";
}

interface SourceTimingDetailsProps {
  sourceType: PayableItem["sourceType"];
  shiftStartsAt: Date | string | null;
  shiftEndsAt: Date | string | null;
  expenseSubmittedAt: Date | string | null;
}

function SourceTimingDetails({
  sourceType,
  shiftStartsAt,
  shiftEndsAt,
  expenseSubmittedAt,
}: SourceTimingDetailsProps) {
  if (sourceType === "expense") {
    return (
      <div className="text-xs text-muted-foreground">
        Submitted {formatDate(expenseSubmittedAt)}
      </div>
    );
  }

  return (
    <div className="whitespace-nowrap">
      <div className="font-medium">{formatDateTime(shiftStartsAt)}</div>
      <div className="text-sm">to {formatDateTime(shiftEndsAt)}</div>
    </div>
  );
}

/**
 * Escape a single CSV cell value. Wraps in double-quotes when the value
 * contains a comma, double-quote, or newline, and escapes embedded quotes.
 */
function escapeCsvValue(value: string | null | undefined): string {
  const str = value == null ? "" : String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string and trigger a browser download.
 */
function downloadCsv(
  filename: string,
  headers: string[],
  rows: string[][],
): void {
  const csvRows = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ];
  const blob = new Blob([csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Parse a date-input string (YYYY-MM-DD) to a local noon Date to avoid
 * UTC-midnight timezone issues.
 */
function parseDateInputValue(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/**
 * Convert a Date to a YYYY-MM-DD string suitable for a date input's value.
 */
function toDateInputValue(value: Date | string | null): string {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// InvoiceDateCell — inline-edit cell for invoiceSentAt / invoicePaidAt
// ---------------------------------------------------------------------------

type InvoiceField = "invoiceSentAt" | "invoicePaidAt";

interface InvoiceDateCellProps {
  payoutId: string;
  field: InvoiceField;
  value: Date | string | null;
  label: string;
  onSaved: () => void;
}

function InvoiceDateCell({
  payoutId,
  field,
  value,
  label,
  onSaved,
}: InvoiceDateCellProps) {
  const [editing, setEditing] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const updateMutation = useMutation(
    trpc.providerPayout.updatePayoutInvoiceStatus.mutationOptions({
      onSuccess: () => {
        setEditing(false);
        onSaved();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const startEdit = () => {
    setInputValue(toDateInputValue(value));
    setEditing(true);
  };

  const save = (newInputValue: string) => {
    if (updateMutation.isPending) {
      return;
    }
    const dateValue = newInputValue ? parseDateInputValue(newInputValue) : null;
    updateMutation.mutate({
      providerPayoutId: payoutId,
      [field]: dateValue,
    });
  };

  const clear = () => {
    if (updateMutation.isPending) {
      return;
    }
    updateMutation.mutate({
      providerPayoutId: payoutId,
      [field]: null,
    });
  };

  const markToday = () => {
    if (updateMutation.isPending) {
      return;
    }
    const today = new Date();
    updateMutation.mutate({
      providerPayoutId: payoutId,
      [field]: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        12,
        0,
        0,
        0,
      ),
    });
  };

  const cancel = () => {
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="date"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              save(inputValue);
            }
            if (e.key === "Escape") {
              cancel();
            }
          }}
          className="h-7 py-0.5 w-36 px-1 text-xs"
          autoFocus
          disabled={updateMutation.isPending}
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5 text-xs"
          disabled={updateMutation.isPending || !inputValue}
          onClick={() => save(inputValue)}
        >
          {updateMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            "Save"
          )}
        </Button>
        {value && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-1.5 text-xs text-muted-foreground"
            disabled={updateMutation.isPending}
            onClick={clear}
          >
            Clear
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          disabled={updateMutation.isPending}
          onClick={cancel}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (value) {
    return (
      <div className="flex items-center gap-1 group">
        <span className="text-sm">{formatDate(value)}</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={startEdit}
          aria-label={`Edit ${label}`}
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm text-muted-foreground">&mdash;</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
        onClick={markToday}
        disabled={updateMutation.isPending}
        aria-label={`Mark ${label} today`}
      >
        {updateMutation.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          "Today"
        )}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
        onClick={startEdit}
        aria-label={`Set ${label}`}
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function ProviderPayoutsPage() {
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();

  // Payable items filters
  const [providerFilter, setProviderFilter] = React.useState(ALL_VALUE);
  const [businessUnitFilter, setBusinessUnitFilter] = React.useState(ALL_VALUE);
  const [offset, setOffset] = React.useState(0);

  // Recent payouts filters
  const [recentProviderFilter, setRecentProviderFilter] =
    React.useState(ALL_VALUE);
  const [recentBusinessUnitFilter, setRecentBusinessUnitFilter] =
    React.useState(ALL_VALUE);
  const [recentStatusFilter, setRecentStatusFilter] = React.useState(ALL_VALUE);
  const [recentCreatedFrom, setRecentCreatedFrom] = React.useState("");
  const [recentCreatedTo, setRecentCreatedTo] = React.useState("");

  const [recentOffset, setRecentOffset] = React.useState(0);
  // Payable items selection (for payout creation / bulk actions)
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  // Recent payouts selection (for CSV export)
  const [selectedPayoutIds, setSelectedPayoutIds] = React.useState<string[]>(
    [],
  );
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [bulkAction, setBulkAction] = React.useState<BulkAction | null>(null);
  const [bulkActionNote, setBulkActionNote] = React.useState("");
  const [externalReference, setExternalReference] = React.useState("");

  const payableItemsQuery = useQuery(
    trpc.providerPayout.listPayableItems.queryOptions({
      providerId: providerFilter === ALL_VALUE ? undefined : providerFilter,
      organizationBusinessUnitId:
        businessUnitFilter === ALL_VALUE ? undefined : businessUnitFilter,
      limit: PAGE_SIZE,
      offset,
    }),
  );

  const payableItemFilterOptionsQuery = useQuery(
    trpc.providerPayout.listPayableItemFilterOptions.queryOptions(),
  );

  const recentPayoutFilterOptionsQuery = useQuery(
    trpc.providerPayout.listRecentPayoutFilterOptions.queryOptions(),
  );

  const recentPayoutsQuery = useQuery(
    trpc.providerPayout.listRecentPayouts.queryOptions({
      providerId:
        recentProviderFilter === ALL_VALUE ? undefined : recentProviderFilter,
      organizationBusinessUnitId:
        recentBusinessUnitFilter === ALL_VALUE
          ? undefined
          : recentBusinessUnitFilter,
      status:
        recentStatusFilter === ALL_VALUE
          ? undefined
          : (recentStatusFilter as ProviderPayoutStatus),
      createdFrom: recentCreatedFrom
        ? parseDateInputValue(recentCreatedFrom)
        : undefined,
      createdTo: recentCreatedTo
        ? (() => {
            const d = parseDateInputValue(recentCreatedTo);
            d.setHours(23, 59, 59, 999);
            return d;
          })()
        : undefined,
      limit: RECENT_PAGE_SIZE,
      offset: recentOffset,
    }),
  );

  const invalidatePayoutQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: trpc.providerPayout.listPayableItems.queryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: trpc.providerPayout.listPayableItemFilterOptions.queryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: trpc.providerPayout.listRecentPayouts.queryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: trpc.providerPayout.getStripeBalance.queryKey(),
    });
  }, [queryClient]);

  const createPayoutMutation = useMutation(
    trpc.providerPayout.createPayout.mutationOptions({
      onSuccess: async (result) => {
        toast.success(
          `Sent payout for ${result.itemCount} item${result.itemCount === 1 ? "" : "s"} to Stripe.`,
        );
        setSelectedIds([]);
        setConfirmOpen(false);
        await invalidatePayoutQueries();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const dismissPayableItemsMutation = useMutation(
    trpc.providerPayout.dismissPayableItems.mutationOptions({
      onSuccess: async (result) => {
        toast.success(
          `Dismissed ${result.itemCount} item${result.itemCount === 1 ? "" : "s"}.`,
        );
        setSelectedIds([]);
        setBulkAction(null);
        setBulkActionNote("");
        await invalidatePayoutQueries();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const markPaidExternallyMutation = useMutation(
    trpc.providerPayout.markPaidExternally.mutationOptions({
      onSuccess: async (result) => {
        toast.success(
          `Marked ${result.itemCount} item${result.itemCount === 1 ? "" : "s"} paid externally.`,
        );
        setSelectedIds([]);
        setBulkAction(null);
        setBulkActionNote("");
        setExternalReference("");
        await invalidatePayoutQueries();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const todayYMD = toDateInputValue(new Date());

  const exportRecentPayoutsMutation = useMutation({
    mutationFn: (payoutIds?: string[]) => {
      if (payoutIds && payoutIds.length > 0) {
        return trpcClient.providerPayout.exportRecentPayouts.mutate({
          payoutIds,
        });
      }
      return trpcClient.providerPayout.exportRecentPayouts.mutate({
        providerId:
          recentProviderFilter === ALL_VALUE ? undefined : recentProviderFilter,
        organizationBusinessUnitId:
          recentBusinessUnitFilter === ALL_VALUE
            ? undefined
            : recentBusinessUnitFilter,
        status:
          recentStatusFilter === ALL_VALUE
            ? undefined
            : (recentStatusFilter as ProviderPayoutStatus),
        createdFrom: recentCreatedFrom
          ? parseDateInputValue(recentCreatedFrom)
          : undefined,
        createdTo: recentCreatedTo
          ? (() => {
              const d = parseDateInputValue(recentCreatedTo);
              d.setHours(23, 59, 59, 999);
              return d;
            })()
          : undefined,
      });
    },
    onSuccess: (data) => {
      const headers = [
        "Payout ID",
        "Provider",
        "Provider Email",
        "Status",
        "Mode",
        "Payment Service",
        "Amount",
        "Currency",
        "Business Units",
        "Item Count",
        "Created",
        "Submitted",
        "Transferred",
        "Paid",
        "Failed",
        "Failure Code",
        "Failure Message",
        "Invoice Sent",
        "Invoice Paid",
        "Stripe Transfer ID",
        "Notes",
      ];
      const rows = data.rows.map((row) => [
        row.id,
        row.providerName,
        row.providerEmail ?? "",
        row.status,
        row.mode,
        row.paymentService ?? "",
        String(row.totalAmount),
        row.currency,
        row.businessUnitNames,
        String(row.itemCount),
        toDateInputValue(row.createdAt),
        toDateInputValue(row.submittedAt),
        toDateInputValue(row.transferredAt),
        toDateInputValue(row.paidAt),
        toDateInputValue(row.failedAt),
        row.failureCode ?? "",
        row.failureMessage ?? "",
        toDateInputValue(row.invoiceSentAt),
        toDateInputValue(row.invoicePaidAt),
        row.externalTransferId ?? "",
        row.notes ?? "",
      ]);
      downloadCsv(`provider-payouts-${todayYMD}.csv`, headers, rows);
      toast.success(
        `Exported ${data.count} payout${data.count === 1 ? "" : "s"} to CSV.`,
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to export payouts",
      );
    },
  });

  const exportPayableItemsMutation = useMutation({
    mutationFn: (payableItemIds?: string[]) => {
      if (payableItemIds && payableItemIds.length > 0) {
        return trpcClient.providerPayout.exportPayableItems.mutate({
          payableItemIds,
        });
      }
      return trpcClient.providerPayout.exportPayableItems.mutate({
        providerId: providerFilter === ALL_VALUE ? undefined : providerFilter,
        organizationBusinessUnitId:
          businessUnitFilter === ALL_VALUE ? undefined : businessUnitFilter,
      });
    },
    onSuccess: (data) => {
      const headers = [
        "Item ID",
        "Provider",
        "Provider Email",
        "Business Unit",
        "Source Type",
        "Source ID",
        "Amount",
        "Currency",
        "Quantity",
        "Unit Amount",
        "Shift Starts",
        "Shift Ends",
        "Expense Submitted",
        "Approved",
        "Available For Payout",
      ];
      const rows = data.rows.map((row) => [
        row.id,
        row.providerName,
        row.providerEmail ?? "",
        row.organizationBusinessUnitName,
        row.sourceType,
        row.sourceId ?? "",
        String(row.amount),
        row.currency,
        row.quantity != null ? String(row.quantity) : "",
        row.unitAmount != null ? String(row.unitAmount) : "",
        row.shiftStartsAt ? new Date(row.shiftStartsAt).toISOString() : "",
        row.shiftEndsAt ? new Date(row.shiftEndsAt).toISOString() : "",
        row.expenseSubmittedAt
          ? new Date(row.expenseSubmittedAt).toISOString()
          : "",
        toDateInputValue(row.approvedAt),
        toDateInputValue(row.availableForPayoutAt),
      ]);
      downloadCsv(`payable-items-${todayYMD}.csv`, headers, rows);
      toast.success(
        `Exported ${data.count} item${data.count === 1 ? "" : "s"} to CSV.`,
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to export items",
      );
    },
  });

  const payableItems = payableItemsQuery.data?.items ?? EMPTY_PAYABLE_ITEMS;
  const totalPayableItems = payableItemsQuery.data?.total ?? 0;
  const hasPreviousPage = offset > 0;
  const hasNextPage = payableItemsQuery.data?.hasMore ?? false;
  const pageStart = totalPayableItems === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + payableItems.length, totalPayableItems);
  const recentPayouts = recentPayoutsQuery.data?.items ?? EMPTY_RECENT_PAYOUTS;
  const totalRecentPayouts = recentPayoutsQuery.data?.total ?? 0;
  const hasPreviousRecentPage = recentOffset > 0;
  const hasNextRecentPage = recentPayoutsQuery.data?.hasMore ?? false;
  const recentPageStart = totalRecentPayouts === 0 ? 0 : recentOffset + 1;
  const recentPageEnd = Math.min(
    recentOffset + recentPayouts.length,
    totalRecentPayouts,
  );

  const providerOptions = payableItemFilterOptionsQuery.data?.providers ?? [];
  const businessUnitOptions =
    payableItemFilterOptionsQuery.data?.businessUnits ?? [];

  const recentProviderOptions =
    recentPayoutFilterOptionsQuery.data?.providers ?? [];
  const recentBusinessUnitOptions =
    recentPayoutFilterOptionsQuery.data?.businessUnits ?? [];

  const hasRecentFilters =
    recentProviderFilter !== ALL_VALUE ||
    recentBusinessUnitFilter !== ALL_VALUE ||
    recentStatusFilter !== ALL_VALUE ||
    recentCreatedFrom !== "" ||
    recentCreatedTo !== "";

  const clearRecentFilters = () => {
    setRecentProviderFilter(ALL_VALUE);
    setRecentBusinessUnitFilter(ALL_VALUE);
    setRecentStatusFilter(ALL_VALUE);
    setRecentCreatedFrom("");
    setRecentCreatedTo("");
    setRecentOffset(0);
  };

  const filteredItems = React.useMemo(() => {
    return payableItems.filter((item) => {
      if (providerFilter !== ALL_VALUE && item.providerId !== providerFilter) {
        return false;
      }
      if (
        businessUnitFilter !== ALL_VALUE &&
        item.organizationBusinessUnitId !== businessUnitFilter
      ) {
        return false;
      }
      return true;
    });
  }, [businessUnitFilter, payableItems, providerFilter]);

  const selectedItems = React.useMemo(() => {
    const selected = new Set(selectedIds);
    return payableItems.filter((item) => selected.has(item.id));
  }, [payableItems, selectedIds]);

  const selectedKey =
    selectedItems.length > 0 ? selectionKey(selectedItems[0]) : null;
  const selectedTotalCents = selectedItems.reduce(
    (sum, item) => sum + item.amountCents,
    0,
  );
  const selectedTotal = selectedTotalCents / 100;
  const selectedProvider = selectedItems[0];
  const providerAccountReady =
    selectedProvider?.providerPaymentAccount.isReady ?? false;
  const selectedCurrency = selectedProvider?.currency ?? "usd";

  const stripeBalanceQuery = useQuery(
    trpc.providerPayout.getStripeBalance.queryOptions(
      { currency: selectedCurrency },
      { enabled: confirmOpen && selectedItems.length > 0 },
    ),
  );
  const hasInsufficientStripeBalance =
    stripeBalanceQuery.data !== undefined &&
    stripeBalanceQuery.data.availableCents < selectedTotalCents;
  const bulkActionNoteTrimmed = bulkActionNote.trim();
  const externalReferenceTrimmed = externalReference.trim();
  const bulkActionPending =
    dismissPayableItemsMutation.isPending ||
    markPaidExternallyMutation.isPending;

  const openBulkAction = (action: BulkAction) => {
    setBulkAction(action);
    setBulkActionNote("");
    setExternalReference("");
  };

  const closeBulkAction = () => {
    if (bulkActionPending) {
      return;
    }
    setBulkAction(null);
    setBulkActionNote("");
    setExternalReference("");
  };

  const submitBulkAction = () => {
    if (!bulkAction || bulkActionNoteTrimmed.length === 0) {
      return;
    }
    const payableItemIds = selectedItems.map((item) => item.id);

    if (bulkAction === "dismiss") {
      dismissPayableItemsMutation.mutate({
        payableItemIds,
        reason: bulkActionNoteTrimmed,
      });
      return;
    }

    markPaidExternallyMutation.mutate({
      payableItemIds,
      note: bulkActionNoteTrimmed,
      externalReference:
        externalReferenceTrimmed.length > 0
          ? externalReferenceTrimmed
          : undefined,
    });
  };

  const canSelectItem = React.useCallback(
    (item: PayableItem) => {
      return !selectedKey || selectedKey === selectionKey(item);
    },
    [selectedKey],
  );

  const selectableFilteredItems = filteredItems.filter(canSelectItem);

  const toggleItem = (item: PayableItem, checked: boolean) => {
    if (checked && !canSelectItem(item)) {
      return;
    }
    setSelectedIds((current) =>
      checked
        ? Array.from(new Set([...current, item.id]))
        : current.filter((id) => id !== item.id),
    );
  };

  const toggleVisibleItems = (checked: boolean) => {
    if (!checked) {
      const visibleIds = new Set(filteredItems.map((item) => item.id));
      setSelectedIds((current) => current.filter((id) => !visibleIds.has(id)));
      return;
    }

    setSelectedIds((current) =>
      Array.from(
        new Set([
          ...current,
          ...selectableFilteredItems.map((item) => item.id),
        ]),
      ),
    );
  };

  const visibleSelectedCount = filteredItems.filter((item) =>
    selectedIds.includes(item.id),
  ).length;
  const allSelectableVisibleSelected =
    selectableFilteredItems.length > 0 &&
    selectableFilteredItems.every((item) => selectedIds.includes(item.id));

  // Recent payouts selection helpers
  const togglePayout = (id: string, checked: boolean) => {
    setSelectedPayoutIds((current) =>
      checked
        ? Array.from(new Set([...current, id]))
        : current.filter((x) => x !== id),
    );
  };

  const toggleAllVisiblePayouts = (checked: boolean) => {
    if (!checked) {
      const visibleIds = new Set(recentPayouts.map((p) => p.id));
      setSelectedPayoutIds((current) =>
        current.filter((id) => !visibleIds.has(id)),
      );
      return;
    }
    setSelectedPayoutIds((current) =>
      Array.from(new Set([...current, ...recentPayouts.map((p) => p.id)])),
    );
  };

  const allVisiblePayoutsSelected =
    recentPayouts.length > 0 &&
    recentPayouts.every((p) => selectedPayoutIds.includes(p.id));

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-title-md2 font-bold text-black">
            Provider Payouts
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review ready payable items and create manual provider payouts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              payableItemsQuery.refetch();
              payableItemFilterOptionsQuery.refetch();
              recentPayoutsQuery.refetch();
            }}
            disabled={
              payableItemsQuery.isFetching || recentPayoutsQuery.isFetching
            }
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${
                payableItemsQuery.isFetching || recentPayoutsQuery.isFetching
                  ? "animate-spin"
                  : ""
              }`}
            />
            Refresh
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={selectedItems.length === 0}
          >
            Create Payout ({selectedItems.length})
          </Button>
          <Button
            variant="outline"
            onClick={() => openBulkAction("external-paid")}
            disabled={selectedItems.length === 0}
          >
            Mark Paid Externally
          </Button>
          <Button
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
            onClick={() => openBulkAction("dismiss")}
            disabled={selectedItems.length === 0}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Dismiss
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border bg-white">
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-[minmax(180px,260px)_minmax(180px,260px)_1fr]">
          <Select
            value={providerFilter}
            onValueChange={(value) => {
              setProviderFilter(value);
              setOffset(0);
              setSelectedIds([]);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All providers</SelectItem>
              {providerOptions.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={businessUnitFilter}
            onValueChange={(value) => {
              setBusinessUnitFilter(value);
              setOffset(0);
              setSelectedIds([]);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Business unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All business units</SelectItem>
              {businessUnitOptions.map((businessUnit) => (
                <SelectItem key={businessUnit.id} value={businessUnit.id}>
                  {businessUnit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-1 lg:justify-end">
            <span className="text-sm text-muted-foreground">
              {selectedItems.length > 0
                ? `${selectedItems.length} selected · ${formatCurrency(selectedTotal)}`
                : `${totalPayableItems} ready item${totalPayableItems === 1 ? "" : "s"}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={exportPayableItemsMutation.isPending}
              onClick={() =>
                exportPayableItemsMutation.mutate(
                  selectedIds.length > 0 ? selectedIds : undefined,
                )
              }
            >
              {exportPayableItemsMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {selectedIds.length > 0
                ? `Export selected (${selectedIds.length})`
                : "Export CSV"}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-[680px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allSelectableVisibleSelected}
                    onCheckedChange={(checked) => toggleVisibleItems(!!checked)}
                    disabled={selectableFilteredItems.length === 0}
                  />
                </TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payableItemsQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No ready payable items match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => {
                  const checked = selectedIds.includes(item.id);
                  const disabled = !checked && !canSelectItem(item);
                  return (
                    <TableRow
                      key={item.id}
                      className={disabled ? "opacity-50" : undefined}
                    >
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={(value) => toggleItem(item, !!value)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{item.providerName}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {item.organizationBusinessUnitName}
                        </div>
                        {!item.providerPaymentAccount.isReady && (
                          <div className="mt-1">
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Account missing
                            </Badge>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.sourceType === "time_entry" ? (
                          <>
                            <SourceTimingDetails
                              sourceType={item.sourceType}
                              shiftStartsAt={item.shiftStartsAt}
                              shiftEndsAt={item.shiftEndsAt}
                              expenseSubmittedAt={item.expenseSubmittedAt}
                            />
                            <div className="text-xs text-muted-foreground">
                              Time entry
                            </div>
                          </>
                        ) : (
                          <>
                            <div>{sourceTypeLabel(item.sourceType)}</div>
                            <div className="text-xs text-muted-foreground">
                              {shortId(item.sourceId)}
                            </div>
                            <SourceTimingDetails
                              sourceType={item.sourceType}
                              shiftStartsAt={item.shiftStartsAt}
                              shiftEndsAt={item.shiftEndsAt}
                              expenseSubmittedAt={item.expenseSubmittedAt}
                            />
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <div>{formatCurrency(item.amount, item.currency)}</div>
                        {item.sourceType === "time_entry" && (
                          <div className="whitespace-nowrap text-xs font-normal text-muted-foreground">
                            {item.quantity ?? "-"} ×{" "}
                            {item.unitAmount
                              ? formatCurrency(item.unitAmount, item.currency)
                              : "-"}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {selectedItems.length > 0 && visibleSelectedCount === 0 && (
          <div className="border-t p-3 text-sm text-muted-foreground">
            Selected items are hidden by the current filters.
          </div>
        )}

        <div className="flex flex-col gap-3 border-t p-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div>
            Showing {pageStart}-{pageEnd} of {totalPayableItems}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPreviousPage || payableItemsQuery.isFetching}
              onClick={() => {
                setOffset(Math.max(0, offset - PAGE_SIZE));
                setSelectedIds([]);
              }}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNextPage || payableItemsQuery.isFetching}
              onClick={() => {
                setOffset(offset + PAGE_SIZE);
                setSelectedIds([]);
              }}
            >
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-md border bg-white">
        <div className="flex flex-col gap-1 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-semibold text-black">Recent Payouts</h3>
            <p className="text-sm text-muted-foreground">
              Recently submitted provider payouts and their included items.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {totalRecentPayouts} payout
              {totalRecentPayouts === 1 ? "" : "s"}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={exportRecentPayoutsMutation.isPending}
              onClick={() =>
                exportRecentPayoutsMutation.mutate(
                  selectedPayoutIds.length > 0 ? selectedPayoutIds : undefined,
                )
              }
            >
              {exportRecentPayoutsMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {selectedPayoutIds.length > 0
                ? `Export selected (${selectedPayoutIds.length})`
                : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Recent Payouts filter row */}
        <div className="grid gap-3 border-b p-4 sm:grid-cols-2 xl:grid-cols-[200px_200px_180px_140px_140px_auto]">
          <Select
            value={recentProviderFilter}
            onValueChange={(value) => {
              setRecentProviderFilter(value);
              setRecentOffset(0);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All providers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All providers</SelectItem>
              {recentProviderOptions.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={recentBusinessUnitFilter}
            onValueChange={(value) => {
              setRecentBusinessUnitFilter(value);
              setRecentOffset(0);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All business units" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All business units</SelectItem>
              {recentBusinessUnitOptions.map((businessUnit) => (
                <SelectItem key={businessUnit.id} value={businessUnit.id}>
                  {businessUnit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={recentStatusFilter}
            onValueChange={(value) => {
              setRecentStatusFilter(value);
              setRecentOffset(0);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
              {PAYOUT_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="date"
            value={recentCreatedFrom}
            onChange={(e) => {
              setRecentCreatedFrom(e.target.value);
              setRecentOffset(0);
            }}
            placeholder="From date"
            aria-label="Created from date"
          />

          <Input
            type="date"
            value={recentCreatedTo}
            onChange={(e) => {
              setRecentCreatedTo(e.target.value);
              setRecentOffset(0);
            }}
            placeholder="To date"
            aria-label="Created to date"
          />

          <div className="flex items-center">
            {hasRecentFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearRecentFilters}
              >
                <X className="mr-1 h-3 w-3" />
                Clear filters
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allVisiblePayoutsSelected}
                    onCheckedChange={(checked) =>
                      toggleAllVisiblePayouts(!!checked)
                    }
                    disabled={recentPayouts.length === 0}
                  />
                </TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Stripe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentPayoutsQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : recentPayouts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No provider payouts have been submitted yet.
                  </TableCell>
                </TableRow>
              ) : (
                recentPayouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedPayoutIds.includes(payout.id)}
                        onCheckedChange={(checked) =>
                          togglePayout(payout.id, !!checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{payout.providerName}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Submitted{" "}
                        {formatDate(
                          payout.submittedAt ??
                            payout.transferredAt ??
                            payout.failedAt ??
                            payout.createdAt,
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(payout.status)}>
                        {payout.status.replaceAll("_", " ")}
                      </Badge>
                      {payout.failureMessage && (
                        <div className="mt-1 max-w-48 text-xs text-red-700">
                          {payout.failureMessage}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(payout.totalAmount, payout.currency)}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {payout.items.map((item) => (
                          <div key={item.id} className="text-sm">
                            {item.sourceType === "time_entry" ? (
                              <>
                                <SourceTimingDetails
                                  sourceType={item.sourceType}
                                  shiftStartsAt={item.shiftStartsAt}
                                  shiftEndsAt={item.shiftEndsAt}
                                  expenseSubmittedAt={item.expenseSubmittedAt}
                                />
                                <div className="text-xs text-muted-foreground">
                                  Time entry ·{" "}
                                  {formatCurrency(item.amount, item.currency)}
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <span>
                                    {sourceTypeLabel(item.sourceType)}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {" "}
                                    {shortId(item.sourceId)} ·{" "}
                                    {formatCurrency(item.amount, item.currency)}
                                  </span>
                                </div>
                                <SourceTimingDetails
                                  sourceType={item.sourceType}
                                  shiftStartsAt={item.shiftStartsAt}
                                  shiftEndsAt={item.shiftEndsAt}
                                  expenseSubmittedAt={item.expenseSubmittedAt}
                                />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div>
                          <div className="text-xs text-muted-foreground">
                            Sent
                          </div>
                          <InvoiceDateCell
                            payoutId={payout.id}
                            field="invoiceSentAt"
                            value={payout.invoiceSentAt}
                            label="Invoice Sent"
                            onSaved={() =>
                              queryClient.invalidateQueries({
                                queryKey:
                                  trpc.providerPayout.listRecentPayouts.queryKey(),
                              })
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            Paid
                          </div>
                          <InvoiceDateCell
                            payoutId={payout.id}
                            field="invoicePaidAt"
                            value={payout.invoicePaidAt}
                            label="Invoice Paid"
                            onSaved={() =>
                              queryClient.invalidateQueries({
                                queryKey:
                                  trpc.providerPayout.listRecentPayouts.queryKey(),
                              })
                            }
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        {payout.stripe.transferUrl && (
                          <a
                            href={payout.stripe.transferUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                          >
                            Transfer
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {payout.stripe.accountUrl && (
                          <a
                            href={payout.stripe.accountUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary hover:underline"
                          >
                            Account
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {!payout.stripe.transferUrl &&
                          !payout.stripe.accountUrl && (
                            <span className="text-sm text-muted-foreground">
                              -
                            </span>
                          )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col gap-3 border-t p-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div>
            Showing {recentPageStart}-{recentPageEnd} of {totalRecentPayouts}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPreviousRecentPage || recentPayoutsQuery.isFetching}
              onClick={() => {
                setRecentOffset(Math.max(0, recentOffset - RECENT_PAGE_SIZE));
              }}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNextRecentPage || recentPayoutsQuery.isFetching}
              onClick={() => {
                setRecentOffset(recentOffset + RECENT_PAGE_SIZE);
              }}
            >
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Provider Payout</DialogTitle>
            <DialogDescription>
              This will create a manual payout for the selected payable items.
            </DialogDescription>
          </DialogHeader>

          {selectedProvider && (
            <div className="space-y-4">
              <div className="grid gap-2 rounded-md border p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Provider</span>
                  <span className="font-medium">
                    {selectedProvider.providerName}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Business unit</span>
                  <span className="font-medium">
                    {selectedProvider.organizationBusinessUnitName}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Items</span>
                  <span className="font-medium">{selectedItems.length}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-medium">
                    {formatCurrency(selectedTotal, selectedProvider.currency)}
                  </span>
                </div>
              </div>

              {stripeBalanceQuery.isLoading ? (
                <div className="flex items-start gap-3 rounded-md border bg-gray-50 p-3 text-sm text-muted-foreground">
                  <Loader2 className="mt-0.5 h-4 w-4 animate-spin" />
                  <div>Checking Stripe platform balance...</div>
                </div>
              ) : stripeBalanceQuery.data ? (
                hasInsufficientStripeBalance ? (
                  <div className="flex items-start gap-3 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4" />
                    <div>
                      <div className="font-medium">
                        Stripe balance is too low
                      </div>
                      <div className="text-yellow-800">
                        Available balance is{" "}
                        {formatCurrency(
                          stripeBalanceQuery.data.availableAmount,
                          stripeBalanceQuery.data.currency,
                        )}
                        , but this payout needs{" "}
                        {formatCurrency(selectedTotal, selectedCurrency)}.
                        Create a top-up in Stripe before sending this payout.
                      </div>
                      <a
                        href={stripeBalanceQuery.data.balanceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1 font-medium text-yellow-950 underline"
                      >
                        Open Stripe balance
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                    <CheckCircle2 className="mt-0.5 h-4 w-4" />
                    <div>
                      <div className="font-medium">
                        Stripe balance sufficient
                      </div>
                      <div className="text-green-800">
                        Available balance is{" "}
                        {formatCurrency(
                          stripeBalanceQuery.data.availableAmount,
                          stripeBalanceQuery.data.currency,
                        )}
                        .
                      </div>
                    </div>
                  </div>
                )
              ) : stripeBalanceQuery.isError ? (
                <div className="flex items-start gap-3 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4" />
                  <div>
                    <div className="font-medium">
                      Could not check Stripe balance
                    </div>
                    <div className="text-yellow-800">
                      Stripe may still reject the transfer if the available
                      platform balance is too low.
                    </div>
                  </div>
                </div>
              ) : null}

              {providerAccountReady ? (
                <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                  <CheckCircle2 className="mt-0.5 h-4 w-4" />
                  <div>
                    <div className="font-medium">Stripe account ready</div>
                    <div className="text-green-800">
                      {
                        selectedProvider.providerPaymentAccount
                          .externalAccountId
                      }
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4" />
                  <div>
                    <div className="font-medium">Stripe account not ready</div>
                    <div className="text-red-800">
                      The provider must finish payment account setup before a
                      payout can be created.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                !providerAccountReady ||
                hasInsufficientStripeBalance ||
                selectedItems.length === 0 ||
                createPayoutMutation.isPending
              }
              onClick={() =>
                createPayoutMutation.mutate({
                  payableItemIds: selectedItems.map((item) => item.id),
                })
              }
            >
              {createPayoutMutation.isPending ? "Creating..." : "Create Payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkAction !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeBulkAction();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "dismiss"
                ? "Dismiss Payable Items"
                : "Mark Paid Externally"}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === "dismiss"
                ? "These items will be voided and removed from the ready payout list."
                : "These items will be recorded as paid outside Saile and removed from the ready payout list."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-2 rounded-md border p-4 text-sm">
              {selectedProvider && (
                <>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">Provider</span>
                    <span className="font-medium">
                      {selectedProvider.providerName}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">Business unit</span>
                    <span className="font-medium">
                      {selectedProvider.organizationBusinessUnitName}
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Items</span>
                <span className="font-medium">{selectedItems.length}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium">
                  {formatCurrency(selectedTotal, selectedCurrency)}
                </span>
              </div>
            </div>

            {bulkAction === "external-paid" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  External reference
                </label>
                <Input
                  value={externalReference}
                  onChange={(event) => setExternalReference(event.target.value)}
                  placeholder="ACH ID, check number, Stripe transfer ID, etc."
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {bulkAction === "dismiss" ? "Dismissal reason" : "Payment note"}
              </label>
              <Textarea
                value={bulkActionNote}
                onChange={(event) => setBulkActionNote(event.target.value)}
                placeholder={
                  bulkAction === "dismiss"
                    ? "Explain why these items should not be paid."
                    : "Describe how and when these items were paid."
                }
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeBulkAction}
              disabled={bulkActionPending}
            >
              Cancel
            </Button>
            <Button
              variant={bulkAction === "dismiss" ? "destructive" : "default"}
              disabled={
                selectedItems.length === 0 ||
                bulkActionNoteTrimmed.length === 0 ||
                bulkActionPending
              }
              onClick={submitBulkAction}
            >
              {bulkActionPending
                ? "Saving..."
                : bulkAction === "dismiss"
                  ? "Dismiss Items"
                  : "Mark Paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute("/_admin/admin/provider-payouts/")({
  component: ProviderPayoutsPage,
});
